import React, { useState, useMemo, useEffect, useRef } from 'react';
import './Sidebar.css';

const eligibilityIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 12l2 2 4-4" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const claimsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </svg>
);

const settingsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const remittancesIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
    <path d="M6 15h4" />
    <path d="M14 15h4" />
  </svg>
);

const dashboardIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="12" width="4" height="9" rx="1" />
    <rect x="10" y="7" width="4" height="14" rx="1" />
    <rect x="17" y="3" width="4" height="18" rx="1" />
  </svg>
);

const usersIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const appointmentsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const arIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 1v22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const backIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5" />
    <polyline points="12,19 5,12 12,5" />
  </svg>
);

const searchIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function PracticeSearchSelector({ practices, selectedPractice, onPracticeChange }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return practices.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [trimmedQuery, practices]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSelect = (p) => {
    if (p) onPracticeChange(p);
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) handleSelect(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  const displayValue = isOpen ? query : (selectedPractice ? selectedPractice.name : '');

  return (
    <div className="practice-search-selector" ref={containerRef}>
      <div className={`practice-search-field${isOpen ? ' open' : ''}`}>
        <span className="practice-search-field-icon">{searchIcon}</span>
        <input
          ref={inputRef}
          type="text"
          className="practice-search-field-input"
          placeholder={selectedPractice ? selectedPractice.name : 'Search practices'}
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { setIsOpen(true); setQuery(''); }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>
      {isOpen && trimmedQuery && (
        <div className="practice-search-field-results">
          {filtered.length === 0 ? (
            <div className="practice-search-field-empty">No matches</div>
          ) : (
            filtered.map((p, idx) => (
              <div
                key={p.id}
                className={
                  'practice-search-field-result' +
                  (idx === activeIndex ? ' active' : '') +
                  (selectedPractice && p.id === selectedPractice.id ? ' current' : '')
                }
                onClick={() => handleSelect(p)}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                {p.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  biller: 'Biller',
  client: 'Client',
};

// Fixed pages visible to client role (not configurable per user)
const CLIENT_PAGES = ['dashboard', 'claims', 'payments', 'eligibility', 'ar', 'practice-details'];

function Sidebar({ activePage, onPageChange, user, onLogout, practices, selectedPractice, onPracticeChange, onBackToPractices }) {
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  const isSuperAdmin = user && user.role === 'super_admin';

  // Build menu items, filtering by role + page access
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: dashboardIcon },
    { id: 'appointments', label: 'Appointments', icon: appointmentsIcon, adminOnly: true },
    { id: 'eligibility', label: 'Eligibility', icon: eligibilityIcon },
    { id: 'claims', label: 'Claims', icon: claimsIcon },
    { id: 'payments', label: 'Payments', icon: remittancesIcon },
    { id: 'ar', label: 'A/R', icon: arIcon },
    // NB Mission Control is deliberately NOT here. It is a cross-practice supervision grid
    // rather than a page about the selected practice, so it lives in the footer beside Admin
    // and is gated on super_admin — see the sidebar-footer below.
    { id: 'practice-details', label: 'Practice Details', icon: settingsIcon },
  ];

  // Remittances is only meaningful when 835 ERAs flow into Atlas through
  // a directly-integrated clearinghouse (Stedi or ClaimMD). Practices with
  // `clearinghouse='other'` route through a third-party we don't ingest
  // ERAs from, so they get nothing useful on this page either. Hide for
  // everyone — admins included — when the practice doesn't use one of our
  // integrated clearinghouses.
  const INTEGRATED_CLEARINGHOUSES = ['stedi', 'claimmd'];
  const hasClearinghouse = !!(
    selectedPractice && INTEGRATED_CLEARINGHOUSES.includes(selectedPractice.clearinghouse)
  );

  // Determine which pages the user can see
  let visibleItems = allMenuItems.filter(
    item => !item.requiresClearinghouse || hasClearinghouse
  );

  if (!isAdmin && user) {
    if (user.role === 'client') {
      // Clients get a fixed page set — not configurable per user
      visibleItems = visibleItems.filter(item => CLIENT_PAGES.includes(item.id));
    } else {
      // Billers see all pages for their assigned practices (except admin-only)
      visibleItems = visibleItems.filter(item => !item.adminOnly);
    }
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img src="/logo.png" alt="Atlas Billers" className="sidebar-logo" />
      </div>

      {/* Practice Selector — hidden for clients with a single practice */}
      {practices && practices.length > 0 && (isAdmin || practices.length > 1) && (
        <div className="practice-selector">
          <label className="practice-label">Practice</label>
          {isAdmin ? (
            <PracticeSearchSelector
              practices={practices}
              selectedPractice={selectedPractice}
              onPracticeChange={onPracticeChange}
            />
          ) : (
            <select
              className="practice-dropdown"
              value={selectedPractice ? selectedPractice.id : ''}
              onChange={(e) => {
                const practice = practices.find(p => p.id === parseInt(e.target.value));
                if (practice) onPracticeChange(practice);
              }}
            >
              {!selectedPractice && <option value="">Select a practice</option>}
              {practices.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {isAdmin && onBackToPractices && (
            <button className="back-to-practices" onClick={onBackToPractices}>
              {backIcon}
              <span>All Practices</span>
            </button>
          )}
        </div>
      )}

      {/* Client practice label — shown when dropdown is hidden */}
      {selectedPractice && !(isAdmin || practices.length > 1) && (
        <div className="client-practice-label">Client: {selectedPractice.name}</div>
      )}

      <nav className="sidebar-nav">
        {visibleItems.map((item) => (
          <button
            key={item.id}
            className={`sidebar-item ${activePage === item.id ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
            onClick={() => !item.disabled && onPageChange(item.id)}
            disabled={item.disabled}
          >
            <span className="sidebar-icon">{item.icon}</span>
            <span className="sidebar-label">{item.label}</span>
            {item.disabled && <span className="coming-soon">Soon</span>}
          </button>
        ))}
      </nav>

      <div className="sidebar-footer">
        {user && (
          <div className="user-section">
            <div className="user-info">
              <span className="user-email">{user.name || user.email}</span>
              <span className="role-badge" data-role={user.role}>{ROLE_LABELS[user.role] || user.role}</span>
            </div>
            {isSuperAdmin && (
              <button className="logout-button" onClick={() => onPageChange('admin')}>
                {usersIcon}
                Admin
              </button>
            )}
            {/* Between Admin and Sign Out, super_admin only, matching the backend's
                _MINIMUM_ROLE. Here rather than in the nav above because it spans every
                practice rather than describing the selected one, so it stays reachable
                whether or not a practice is open. */}
            {isSuperAdmin && (
              <button
                className={`logout-button ${activePage === 'mission-control' ? 'active' : ''}`}
                onClick={() => onPageChange('mission-control')}
              >
                {dashboardIcon}
                Mission Control
              </button>
            )}
            {/* Rules engine admin — admin-level (billing experts author + review
                rules; backend enforces per-endpoint), with activation and
                practice-mode changes held at super_admin inside the page.
                Cross-practice footer placement matches Mission Control. Billers
                meet rules only as ledger chips + receipts, never this editor. */}
            {isAdmin && (
              <button
                className={`logout-button ${activePage === 'rules' ? 'active' : ''}`}
                onClick={() => onPageChange('rules')}
              >
                {settingsIcon}
                Rules
              </button>
            )}
            <button className="logout-button" onClick={onLogout}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        )}
        <span className="version">v1.2.0</span>
      </div>
    </aside>
  );
}

export default Sidebar;
